import Nuxeo from 'nuxeo'
import
{
    NuxeoRegistrationRequest,
    NuxeoActivationRequest,
    NuxeoGetByEmailRequest,
    NuxeoUpdateUserRequest,
    NuxeoUpdatePasswordRequest,
    NuxeoRetrievePasswordTokenRequest,
    NuxeoRequestConfig,
    NuxeoSearchRequest,
    NuxeoCalSearchRequest,
    NuxeoOrdersRequest,
    NuxeoCalOrderRequest,
    NuxeoOrderRequest,
    NuxeoDocumentsDownloadedRequest
} from '@/contracts/api/RequestContracts'
import
{
    NuxeoRegistrationResponse,
    NuxeoRetrieveDocumentResponse,
    NuxeoActivationResponse,
    NuxeoGetByEmailResponse,
    NuxeoLoginCombinedResponse,
    NuxeoLoginResponse,
    NuxeoUpdateUserResponse,
    NuxeoUpdatePasswordResponse,
    NuxeoRetrievePasswordTokenResponse,
    NuxeoPageProviderResponse,
    NuxeoOrderResponse,
    NuxeoOrder422Response,
    NuxeoDocumentsDownloadedResponse,
    NuxeoErrorResponse,
    PortalConfigFromNuxeoResponse
 } from '@/contracts/api/ResponseContracts'
import { NuxeoErrorMap } from '@/contracts/api/StructureContracts'
import router from '@/./router'
import store from '@/./store'

// Get Notice Details:
export function getPortalConfigFromNuxeo(callback: (response: PortalConfigFromNuxeoResponse) => void): void
{
   executeAutomation('AC_Portal_GetConfiguration')
   .execute()
   .then(function(response: PortalConfigFromNuxeoResponse)
   {
       // Log in non-production:
       if(process.env.NODE_ENV !== 'production')
       {
           console.log(response)
       }
       callback(response)
   })
   .catch(handleError)
}

// Nuxeo registration:
export function nuxeoRegistration(params: NuxeoRegistrationRequest, callback: (response: NuxeoRegistrationResponse) => void): void
{
    executeAutomation('AC_ExternalUserAccess_RegisterUser')
    .params(params)
    .execute()
    .then(function(response: NuxeoRegistrationResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo get document:
export function nuxeoRetrieveDocument(request: string, headers: Record<string, string>, callback: (response: NuxeoRetrieveDocumentResponse) => void): void
{
    executeRequest(request,
    {
        headers: headers
    })
    .get()
    .then(function(response: NuxeoRetrieveDocumentResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo get password document:
export function nuxeoRetrievePasswordToken(params: NuxeoRetrievePasswordTokenRequest, callback: (response: NuxeoRetrievePasswordTokenResponse) => void): void
{
    const nuxeo = initNuxeo()
    nuxeo.repository(
    {
        headers:
        {
            // 'X-NXproperties': 'ua_passwordresetrequest'
        }
    })
    .query(
    {
        query: "SELECT * FROM Document WHERE ua_passwordresetrequest:passwordResetToken = '" + params.docid + "'"
    })
    .then(function(response: NuxeoRetrievePasswordTokenResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo activation:
export function nuxeoActivation(params: NuxeoActivationRequest, callback: (response: NuxeoActivationResponse) => void): void
{
    executeAutomation('AC_ExternalUserAccess_ActivateUser')
    .params(params)
    .execute()
    .then(function(response: NuxeoActivationResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo get user by email:
export function nuxeoGetByEmail(params: NuxeoGetByEmailRequest, callback: (response: NuxeoGetByEmailResponse) => void, customMaps?: Array<NuxeoErrorMap>): void
{
    // Set error maps:
    const maps =
    [
        {
            code: 404,
            error: '404 returned for',
            message: 'Your email address could not be found as your activation link has expired. Please create an account and activate within 2 days.'
        },
        {
            code: 404,
            error: 'Failed to find user: email',
            message: 'Email not found.'
        }
    ]
    executeAutomation('AC_ExternalUserAccess_FindUser')
    .params(params)
    .execute()
    .then(function(response: NuxeoGetByEmailResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(function(error: NuxeoErrorResponse)
    {
        handleError(error, maps, customMaps)
    })
}

// Nuxeo login user:
export function nuxeoLogin(username: string, password: string, callback: (response: NuxeoLoginResponse) => void, customMaps?: Array<NuxeoErrorMap>): void
{
    // Set error maps:
    const maps =
    [
        {
            code: 404,
            error: 'HTTP/1.1 401',
            message: 'Account not found.'
        }
    ]
    new Nuxeo(
    {
        baseURL: process.env.VUE_APP_API_DOMAIN,
        auth:
        {
            method: 'basic',
            username: username,
            password: password
        }
    })
    .connect()
    .then(function(response: NuxeoLoginCombinedResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response.user)
    })
    .catch(function(error: NuxeoErrorResponse)
    {
        handleError(error, maps, customMaps)
    })
}

// Nuxeo update user:
export function nuxeoUpdateUser(params: NuxeoUpdateUserRequest, callback: (response: NuxeoUpdateUserResponse) => void): void
{
    executeAutomation('AC_ExternalUserAccess_UpdateUser')
    .params(params)
    .execute()
    .then(function(response: NuxeoUpdateUserResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo update password:
export function nuxeoUpdatePassword(params: NuxeoUpdatePasswordRequest, callback: (response: NuxeoUpdatePasswordResponse) => void, customMaps?: Array<NuxeoErrorMap>): void
{
    // Set error maps:
    const maps =
    [
        {
            code: 409,
            error: 'Failed to reset password: email',
            message: 'The email address ' + params.email + ' has an outstanding password reset request'
        }
    ]
    executeAutomation('AC_ExternalUserAccess_ResetPassword')
    .params(params)
    .execute()
    .then(function(response: NuxeoUpdatePasswordResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(function(error: NuxeoErrorResponse)
    {
        handleError(error, maps, customMaps)
    })
}

// Nuxeo orders:
export function nuxeoOrders(username: string, password: string, params: NuxeoOrdersRequest, callback: (response: NuxeoPageProviderResponse) => void): void
{
    new Nuxeo(
    {
        baseURL: process.env.VUE_APP_API_DOMAIN,
        auth:
        {
            method: 'basic',
            username: username,
            password: password
        },
        headers:
        {
            'X-NXproperties': '*'
        }
    })
    .request('/automation/javascript.AS_AC_Get_All_Ordered_Grants')
    .post(
    {
        body:
        {
            params: params,
            context: {}
        }
    })
    .then(function(response: NuxeoPageProviderResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo search:
export function nuxeoSearch(params: NuxeoSearchRequest, callback: (response: NuxeoPageProviderResponse) => void): void
{
    const nuxeo = initNuxeo()
    nuxeo.repository(
    {
        headers:
        {
            properties: 'hmcts_grant_schema',
            skipAggregates: true
        }
    })
    .query(Object.assign(
    {
        pageProvider: 'pp_mainstream_default_search'
    }, params))
    .then(function(response: NuxeoPageProviderResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo cal search:
export function nuxeoCalSearch(params: NuxeoCalSearchRequest, callback: (response: NuxeoPageProviderResponse) => void): void
{
    const nuxeo = initNuxeo()
    nuxeo.repository(
    {
        headers:
        {
            properties: 'hmcts_calendarindex_schema,file',
            'fetch-document': 'properties',
            skipAggregates: true
        }
    })
    .query(Object.assign(
    {
        pageProvider: 'pp_cowindex_default_search'
    }, params))
    .then(function(response: NuxeoPageProviderResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo order complete:
export function nuxeoOrderComplete(username: string, password: string, params: NuxeoOrderRequest, callback: (response: NuxeoOrderResponse) => void, customMaps?: Array<NuxeoErrorMap>): void
{
    // Set error maps:
    const maps = [] as Array<NuxeoErrorMap>
    new Nuxeo(
    {
        baseURL: process.env.VUE_APP_API_DOMAIN,
        auth:
        {
            method: 'basic',
            username: username,
            password: password
        }
    })
    .operation('javascript.AS_API_Create_Order_From_Website')
    .params(params)
    .execute()
    .then(function(response: NuxeoOrderResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(function(error: NuxeoErrorResponse)
    {
        handleError(error, maps, customMaps)
    })
}

// Nuxeo cal order complete:
export function nuxeoCalCreateGrant(params: NuxeoCalOrderRequest, callback: (response: NuxeoOrderResponse) => void, callback422: (response: NuxeoOrder422Response) => void): void
{
    new Nuxeo(
    {
        baseURL: process.env.VUE_APP_API_DOMAIN,
        auth:
        {
            method: 'basic'
        }
    })
    .operation('javascript.AS_Grant_RequestNewWill')
    .params(params)
    .execute(
    {
        headers:
        {
            properties: 'hmcts_grant_schema',
            skipAggregates: true
        }
    })
    .then(function(response: NuxeoOrderResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(function(error: NuxeoErrorResponse)
    {
        if(error.response.status === 422)
        {
            error.response.text().then(function(body)
            {
                // Set error:
                const errorMesssage = JSON.parse(body)
                callback422(errorMesssage)
            })
        }
        else
        {
            handleError(error)
        }
    })
}

// Nuxeo get customer orders:
export function nuxeoCustomerOrders(username: string, password: string, callback: (response: NuxeoPageProviderResponse) => void)
{
    new Nuxeo(
    {
        baseURL: process.env.VUE_APP_API_DOMAIN,
        auth:
        {
            method: 'basic',
            username: username,
            password: password
        }
    })
    .http(
    {
        method: 'get',
        headers:
        {
            'X-NXproperties': 'hmcts_order_schema,dublincore'
        },
        url: process.env.VUE_APP_API_DOMAIN + '/api/v1/search/lang/NXQL/execute?query=SELECT * FROM Document WHERE ecm:primaryType = "hmcts_order_collection" AND hmctsorder:customerID = "' + username + '" AND ecm:isProxy = 0 AND ecm:isVersion = 0 AND ecm:isTrashed = 0'
    })
    .then(function(response: NuxeoPageProviderResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo documents downloaded:
export function nuxeoDocumentsDownloaded(username: string, password: string, params: NuxeoDocumentsDownloadedRequest, input: string, callback: (response: NuxeoDocumentsDownloadedResponse) => void): void
{
    new Nuxeo(
    {
        baseURL: process.env.VUE_APP_API_DOMAIN,
        auth:
        {
            method: 'basic',
            username: username,
            password: password
        }
    })
    .operation('javascript.AS_Check_And_Set_Expiry_Date_For_Order_Item')
    .params(params)
    .input(input)
    .execute()
    .then(function(response: NuxeoDocumentsDownloadedResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Init nuxeo client:
function initNuxeo()
{
    return new Nuxeo(
    {
        baseURL: process.env.VUE_APP_API_DOMAIN
    })
}

// Execute automation:
function executeAutomation(operation: string): any
{
    const nuxeo = initNuxeo()
    return nuxeo.operation(operation)
}

// Execute request:
function executeRequest(request: string, config: NuxeoRequestConfig): any
{
    const nuxeo = initNuxeo()
    return nuxeo.request(request, config)
}

// Handle error in axios:
function handleError(error: NuxeoErrorResponse, errorMappings?: Array<NuxeoErrorMap>, customErrorMappings?: Array<NuxeoErrorMap>): void
{
    // Concat custom maps:
    if(errorMappings !== undefined && customErrorMappings !== undefined)
    {
        errorMappings = customErrorMappings.concat(errorMappings)
    }
    // Look for response in error:
    if(error.response)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(error.response)
        }
        error.response.text().then(function(body)
        {
            // Set error:
            const errorMesssage = JSON.parse(body)
            // Check status to override defaults:
            switch(error.response.status)
            {
                case 500:
                    errorMesssage.message = 'HTTP/1.1 500 returned - please contact your administrator.'
                    break
                case 405:
                    errorMesssage.message = 'HTTP/1.1 405 returned - method not allowed.'
                    break
            }
            // Look for mappings:
            if(errorMappings !== undefined)
            {
                errorMappings.every(function(map)
                {
                    console.log(errorMesssage.message, map.error)
                    if(errorMesssage.message.includes(map.error))
                    {
                        errorMesssage.message = map.message
                        if(map.field !== undefined)
                        {
                            errorMesssage.field = map.field
                        }
                        return false
                    }
                    return true
                })
            }
            // Display error:
            store.commit('errors/setNuxeoErrors', errorMesssage)
            router.push({ hash: '#error-summary-container' })
        })
    }
}
